import { observable, makeObservable, action, runInAction, computed } from "mobx";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_ASSEMBLY_PROTOCOL_FILE } from "src/api/assembly";
import { GetAssemblyProtocolFile } from "src/api/generated/GetAssemblyProtocolFile";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, getRoleId, Role } from "src/network/User";
import { FILE_ENTITY, FILE_TYPE } from "src/enums";
import { IFile } from "src/types";
import { GetGenerateNewProtocolData } from "src/api/generated/GetGenerateNewProtocolData";
import {
    DELETE_SIGNATURE,
    DELETE_SIGNATUREFILES,
    GET_ASSEMBLY_SIGNATURE_FILES,
    GET_GENERATE_NEW_PROTOCOL_DATA,
    GET_SIGNATURES_FOR_PROTOCOL,
    INSERT_SIGNATURES
} from "src/api/protocol";
import { UPDATE_ASSEMBLY_PROTOCOL } from "src/api/assembly";
import { UpdateAssemblyProtocol, UpdateAssemblyProtocolVariables } from "src/api/generated/UpdateAssemblyProtocol";
import { format } from "date-fns-tz";
import { getDateForHasuraQuery } from "src/utils/Date";
import Axios from "src/network/Axios";
import { v4 as uuidv4 } from "uuid";
import { DeleteSignature, DeleteSignatureVariables } from "src/api/generated/DeleteSignature";
import { InsertSignatures, InsertSignaturesVariables } from "src/api/generated/InsertSignatures";
import {
    GetAssemblySignatureFiles,
    GetAssemblySignatureFiles_file_v_assemblysignatures,
    GetAssemblySignatureFilesVariables
} from "src/api/generated/GetAssemblySignatureFiles";
import { DeleteSignatureFiles, DeleteSignatureFilesVariables } from "src/api/generated/DeleteSignatureFiles";
import { ensurePdfExtension, sanitizeAssemblyTitleForFileName } from "src/utils/Common";

export interface ISignatureData {
    id: string;
    title: string;
    text: string;
}

export class ProtocolStore {
    rootStore: RootStore;
    initialAssemblyProtocol: {
        protocolauthor: string | null;
        protocoldate: string | null;
        protocolplace: string | null;
    } | null = null;
    protocolDate: string = "";
    protocolPlace: string = "";
    protocolFileId: string = "";
    protocolFileURL: string = "";
    isProtocolPdfLoading: boolean = true;
    assemblyDataHasChanged: boolean = false;
    isProtocolPDFDownloading: boolean = false;
    signaturesList: ISignatureData[] = [];
    originalSignaturesList: ISignatureData[] = [];
    signatureFiles: GetAssemblySignatureFiles_file_v_assemblysignatures[] = [];
    isLoadingSignatures: boolean = false;
    selectedSignatureImageFile: File | undefined = undefined;
    isUploadingSignatureImage: boolean = false;
    private currentAssemblyId: string = "";

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            initialAssemblyProtocol: observable,
            protocolDate: observable,
            protocolPlace: observable,
            protocolFileId: observable,
            isProtocolPdfLoading: observable,
            isProtocolPDFDownloading: observable,
            protocolFileURL: observable,
            signaturesList: observable,
            originalSignaturesList: observable,
            signatureFiles: observable,

            hasSignaturesChanged: computed,
            hasProtocolDatePlaceChanged: computed,

            discardAssemblyProtocol: action,
            generateAssemblyProtocolPdf: action,
            downloadAssemblyProtocolPdf: action,
            checkIfAssemblyDataHasChanged: action,
            checkAndDownloadAssemblyProtocolPdf: action,
            getAssemblyProtocolFile: action,
            downloadAssemblyProtocolFile: action,
            loadSignaturesForProtocol: action,
            loadSignatureFiles: action,
            saveSignatures: action,
            saveProtocolDatePlace: action,
            addSignature: action,
            deleteSignature: action,

            setInitialProtocol: action,
            setProtocolFileId: action,
            setAssemblyDataHasChanged: action,
            setIsProtocolPDFDownloading: action,
            setProtocolFileURL: action,
            setIsProtocolPdfLoading: action,
            setSignaturesList: action,
            setOriginalSignaturesList: action,
            setSignatureFiles: action,
            setSelectedSignatureImageFile: action,
            setProtocolDate: action,
            setProtocolPlace: action,
            uploadAssemblySignatureImage: action,

            selectedSignatureImageFile: observable,
            isUploadingSignatureImage: observable
        });
    }

    get hasSignaturesChanged(): boolean {
        const a = this.signaturesList;
        const b = this.originalSignaturesList;
        if (a.length !== b.length) return true;
        for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id || a[i].title !== b[i].title || a[i].text !== b[i].text) {
                return true;
            }
        }
        return false;
    }

    get hasProtocolDatePlaceChanged(): boolean {
        return (
            this.protocolDate !== (this.initialAssemblyProtocol?.protocoldate ?? "") ||
            this.protocolPlace !== (this.initialAssemblyProtocol?.protocolplace ?? "")
        );
    }

    discardAssemblyProtocol = () => {
        this.setSignaturesList(JSON.parse(JSON.stringify(this.originalSignaturesList)));
        this.setProtocolDate(this.initialAssemblyProtocol?.protocoldate ?? "");
        this.setProtocolPlace(this.initialAssemblyProtocol?.protocolplace ?? "");
    };

    saveSignatures = async (assemblyId: string) => {
        this.signaturesList.forEach((signature) => {
            if (!signature.id || signature.id.trim().length === 0) {
                signature.id = uuidv4();
            }
        });

        const insertBody: InsertSignaturesVariables = {
            objects: this.signaturesList.map((signature) => ({
                id: signature.id,
                assemblyid: assemblyId,
                title: signature.title ?? "",
                text: signature.text ?? ""
            }))
        };

        try {
            await apolloClientInstance.mutate<InsertSignatures, InsertSignaturesVariables>({
                mutation: INSERT_SIGNATURES,
                variables: insertBody
            });

            this.setOriginalSignaturesList(JSON.parse(JSON.stringify(this.signaturesList)));

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.save_signature_success"),
                MessageType.SUCCESS
            );
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.save_signature_error"),
                MessageType.ERROR
            );

            console.error(i18next.t("screens.assemblies.protocolcontent.save_signature_error: ", error));
        }
    };

    saveProtocolDatePlace = async (assemblyId: string) => {
        try {
            await apolloClientInstance.mutate<UpdateAssemblyProtocol, UpdateAssemblyProtocolVariables>({
                mutation: UPDATE_ASSEMBLY_PROTOCOL,
                variables: {
                    assemblyid: assemblyId,
                    protocoldate: this.protocolDate || null,
                    protocolplace: this.protocolPlace || null
                }
            });

            runInAction(() => {
                if (this.initialAssemblyProtocol) {
                    this.initialAssemblyProtocol.protocoldate = this.protocolDate;
                    this.initialAssemblyProtocol.protocolplace = this.protocolPlace;
                }
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.save_success"),
                MessageType.SUCCESS
            );
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.save_error"),
                MessageType.ERROR
            );

            console.error("Failed to save protocol date/place:", error);
        }
    };

    downloadAssemblyProtocolFile = async (fileId: string): Promise<any | undefined> => {
        let downloadedAssemblyProtocolFile = undefined;

        try {
            const response = await Axios.getAuthInstance().get(`${NetworkConfig.downloadFileVersion2}/${fileId}`, {
                urlWithoutParams: NetworkConfig.downloadFileVersion2,
                responseType: "blob",
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            downloadedAssemblyProtocolFile = response;
        } catch (error) {
            this.setProtocolFileId("");
            this.setProtocolFileURL("");
            console.error("Error occured while trying to download the assemblyProtocol file: ", error);
        }

        return downloadedAssemblyProtocolFile;
    };

    /**
     * Lightweight existence check: only queries for the protocol file row, without downloading
     * the PDF blob. Use this instead of getAssemblyProtocolFile when only the existence of a
     * protocol PDF matters, so a failed/slow blob download can't be mistaken for "no protocol".
     */
    hasAssemblyProtocolFile = async (assemblyId: string): Promise<boolean> => {
        try {
            const { data }: { data: GetAssemblyProtocolFile } =
                await apolloClientInstance.query<GetAssemblyProtocolFile>({
                    query: GET_ASSEMBLY_PROTOCOL_FILE,
                    fetchPolicy: "no-cache",
                    variables: {
                        assemblyid: assemblyId
                    }
                });

            return !!(data.file_assemblyfiles && data.file_assemblyfiles.length > 0);
        } catch (error) {
            console.error("Failed to check if assembly protocol file exists:", error);
            return false;
        }
    };

    getAssemblyProtocolFile = async (assemblyId: string) => {
        this.setIsProtocolPdfLoading(true);
        try {
            const { data }: { data: GetAssemblyProtocolFile } =
                await apolloClientInstance.query<GetAssemblyProtocolFile>({
                    query: GET_ASSEMBLY_PROTOCOL_FILE,
                    fetchPolicy: "no-cache",
                    variables: {
                        assemblyid: assemblyId
                    }
                });

            if (data.file_assemblyfiles && data.file_assemblyfiles.length > 0) {
                this.setProtocolFileId(data.file_assemblyfiles[0].id);

                const downloadedAssemblyProtocolFile = await this.downloadAssemblyProtocolFile(
                    data.file_assemblyfiles[0].fileid
                );

                if (downloadedAssemblyProtocolFile?.data) {
                    const fileBlob = downloadedAssemblyProtocolFile.data;
                    const objUrl = window.URL.createObjectURL(fileBlob);

                    // Revoke old URL if it exists
                    if (this.protocolFileURL) {
                        window.URL.revokeObjectURL(this.protocolFileURL);
                    }

                    this.setProtocolFileURL(objUrl);
                }
            } else {
                this.setProtocolFileId("");
                this.setProtocolFileURL("");
            }
        } catch (error) {
            this.setProtocolFileId("");
            this.setProtocolFileURL("");
            console.error("Error loading assembly protocol file:", error);
        }

        this.setIsProtocolPdfLoading(false);
    };

    checkAndDownloadAssemblyProtocolPdf = async (assemblyId: string) => {
        if (this.isProtocolPDFDownloading) return;

        this.setIsProtocolPDFDownloading(true);
        try {
            await this.checkIfAssemblyDataHasChanged(assemblyId);

            if (this.assemblyDataHasChanged || this.protocolFileId === "") {
                await this.generateAssemblyProtocolPdf(assemblyId);
                this.setAssemblyDataHasChanged(false);
            }

            // Download before getAssemblyProtocolFile, which overwrites protocolFileId
            // with the assemblyfile record ID (not the download fileid)
            if (this.protocolFileId !== "") {
                await this.downloadAssemblyProtocolPdf();
            }

            await this.getAssemblyProtocolFile(assemblyId);
        } finally {
            this.setIsProtocolPDFDownloading(false);
        }
    };

    checkIfAssemblyDataHasChanged = async (assemblyId: string) => {
        try {
            const { data }: { data: GetGenerateNewProtocolData } =
                await apolloClientInstance.query<GetGenerateNewProtocolData>({
                    query: GET_GENERATE_NEW_PROTOCOL_DATA,
                    variables: { assemblyid: assemblyId },
                    fetchPolicy: "no-cache"
                });

            runInAction(() => {
                if (data.file_v_generate_new_protocol && data.file_v_generate_new_protocol.length > 0) {
                    this.setProtocolFileId(data.file_v_generate_new_protocol[0].fileid);
                    this.setAssemblyDataHasChanged(data.file_v_generate_new_protocol[0].generate_new_protocol || false);
                }
            });
        } catch (error) {
            console.error("Failed to load data from view file_v_generate_new_protocol:", error);
            throw error;
        }
    };

    generateAssemblyProtocolPdf = async (assemblyId: string) => {
        try {
            const response = await Axios.getAuthInstance().post(
                NetworkConfig.generateassemblyprotocolpdf + assemblyId,
                undefined,
                {
                    urlWithoutParams: NetworkConfig.generateassemblyprotocolpdf,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const result = response.data;
            this.setProtocolFileId(JSON.parse(result.data));
        } catch (error) {
            this.setProtocolFileId("");
            console.error("Error while trying to call generateassemblyprotocolpdf: ", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.generate_error") + error,
                MessageType.ERROR
            );
        }
    };

    downloadAssemblyProtocolPdf = async () => {
        const response = await this.downloadAssemblyProtocolFile(this.protocolFileId);
        if (!response?.data) return;

        const contentDisposition = response.headers?.["content-disposition"] ?? "";
        const fileNameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        let fileName = fileNameStarMatch ? decodeURIComponent(fileNameStarMatch[1]) : fileNameMatch?.[1];

        if (!fileName) {
            const assemblyDate = this.rootStore.assemblyStore.currentAssembly?.date;
            const date = assemblyDate ? format(new Date(assemblyDate), "dd_MM_yyyy") : "";
            const title = sanitizeAssemblyTitleForFileName(this.rootStore.assemblyStore.currentAssembly?.title);
            fileName = `Protokoll_${title}_am_${date}`;
        }
        fileName = ensurePdfExtension(fileName);

        const objUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    loadSignaturesForProtocol = async (assemblyId: string) => {
        this.currentAssemblyId = assemblyId;
        this.isLoadingSignatures = true;
        try {
            const { data } = await apolloClientInstance.query<{ cond_signatures: ISignatureData[] }>({
                query: GET_SIGNATURES_FOR_PROTOCOL,
                variables: { assemblyid: assemblyId },
                fetchPolicy: "no-cache"
            });

            runInAction(() => {
                this.setSignaturesList(data.cond_signatures ?? []);
                if (this.signaturesList.length === 0) {
                    this.addSignature();
                }
                this.setOriginalSignaturesList(JSON.parse(JSON.stringify(this.signaturesList)));
            });

            await this.loadSignatureFiles(assemblyId);
        } catch (error) {
            console.error("Failed to load signatures for protocol:", error);
            this.setSignaturesList([]);
        } finally {
            runInAction(() => {
                this.isLoadingSignatures = false;
            });
        }
    };

    loadSignatureFiles = async (assemblyId: string) => {
        try {
            const { data } = await apolloClientInstance.query<
                GetAssemblySignatureFiles,
                GetAssemblySignatureFilesVariables
            >({
                query: GET_ASSEMBLY_SIGNATURE_FILES,
                variables: {
                    assemblyId,
                    role: getRoleId(this.rootStore.authStore.user?.role ?? Role.NONE)
                },
                fetchPolicy: "no-cache"
            });

            runInAction(() => {
                this.signatureFiles = data.file_v_assemblysignatures ?? [];
            });
        } catch (error) {
            console.error("Failed to load signature files:", error);
        }
    };

    addSignature = () => {
        const newSignature: ISignatureData = {
            id: uuidv4(),
            title: "",
            text: ""
        };
        this.signaturesList = [...this.signaturesList, newSignature];
    };

    deleteSignature = async (signatureId: string) => {
        try {
            if (signatureId !== undefined) {
                await apolloClientInstance.mutate<DeleteSignature, DeleteSignatureVariables>({
                    mutation: DELETE_SIGNATURE,
                    variables: { signatureId: signatureId }
                });

                this.setSignaturesList(this.signaturesList.filter((signature) => signature.id !== signatureId));
            }
        } catch (error: any) {
            console.error("Signature failed to delete. Error: ", error);
        }
    };

    deleteSignatureFiles = async (signatureId: string) => {
        try {
            if (signatureId !== undefined) {
                await apolloClientInstance.mutate<DeleteSignatureFiles, DeleteSignatureFilesVariables>({
                    mutation: DELETE_SIGNATUREFILES,
                    variables: { signatureId: signatureId }
                });

                this.setSignatureFiles(this.signatureFiles.filter((file) => file.signatureid !== signatureId));
            }
        } catch (error: any) {
            console.error("Signature files failed to delete. Error: ", error);
        }
    };

    /* SETTER */

    setInitialProtocol = () => {
        const currentAssembly = this.rootStore.assemblyStore.currentAssembly;
        if (currentAssembly) {
            const formattedProtocolDate = currentAssembly.protocoldate
                ? (getDateForHasuraQuery(currentAssembly.protocoldate) ?? "")
                : "";
            this.initialAssemblyProtocol = {
                protocolauthor: currentAssembly.protocolauthor,
                protocoldate: formattedProtocolDate,
                protocolplace: currentAssembly.protocolplace
            };
            this.protocolDate = formattedProtocolDate;
            this.protocolPlace = currentAssembly.protocolplace ?? "";
        }
    };

    setProtocolDate = (protocolDate: string) => {
        this.protocolDate = protocolDate;
    };

    setProtocolPlace = (protocolPlace: string) => {
        this.protocolPlace = protocolPlace;
    };

    setAssemblyDataHasChanged = (assemblyDataHasChanged: boolean) => {
        this.assemblyDataHasChanged = assemblyDataHasChanged;
    };

    setIsProtocolPDFDownloading = (isProtocolPDFDownloading: boolean) => {
        this.isProtocolPDFDownloading = isProtocolPDFDownloading;
    };

    setProtocolFileId = (protocolFileId: string) => {
        this.protocolFileId = protocolFileId;
    };

    setProtocolFileURL = (protocolFileURL: string) => {
        this.protocolFileURL = protocolFileURL;
    };

    setIsProtocolPdfLoading = (isProtocolPdfLoading: boolean) => {
        this.isProtocolPdfLoading = isProtocolPdfLoading;
    };

    setSignaturesList = (signaturesList: ISignatureData[]) => {
        this.signaturesList = signaturesList;
    };

    setOriginalSignaturesList = (originalSignaturesList: ISignatureData[]) => {
        this.originalSignaturesList = originalSignaturesList;
    };

    setSignatureFiles = (files: GetAssemblySignatureFiles_file_v_assemblysignatures[]) => {
        this.signatureFiles = files;
    };

    setSelectedSignatureImageFile = (file: File | undefined) => {
        this.selectedSignatureImageFile = file;
    };

    private fileToBase64DataOnly = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const result = String(reader.result ?? "");
                const commaIndex = result.indexOf(",");
                resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
            };
            reader.readAsDataURL(file);
        });
    };

    private buildIFileFromBrowserFile = async (file: File, type: number): Promise<IFile> => {
        const base64String = await this.fileToBase64DataOnly(file);
        const extension = (file.name.split(".").pop() ?? "").toLowerCase();
        const filename = file.name.endsWith(`.${extension}`) ? file.name.replace(`.${extension}`, "") : file.name;

        return {
            mimetype: file.type,
            extension,
            name: filename,
            fileDate: new Date().toISOString(),
            base64String,
            type
        } as IFile;
    };

    uploadAssemblySignatureImage = async (signatureId: string) => {
        if (!this.selectedSignatureImageFile) return;

        const file = this.selectedSignatureImageFile;

        this.isUploadingSignatureImage = true;

        try {
            const signatureImageType = FILE_TYPE.ASSEMBLY_SIGNATURE;

            const metadataFile = await this.buildIFileFromBrowserFile(file, signatureImageType);
            metadataFile.fileBlob = file;

            await this.rootStore.fileStore.uploadFilesBulkWithRoles(
                metadataFile,
                signatureId,
                FILE_ENTITY.COND_SIGNATURES,
                [Role.MANAGER, Role.COOWNER]
            );

            runInAction(() => {
                this.selectedSignatureImageFile = undefined;
            });

            await this.loadSignatureFiles(this.currentAssemblyId);

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.upload_image_success"),
                MessageType.SUCCESS
            );
        } catch (e) {
            console.error(e);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.upload_image_error"),
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.isUploadingSignatureImage = false;
            });
        }
    };
}
